import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";
import OrdersController from "../../controllers/orderController";

/**
 * The two steps a merchant takes for a new order:
 *  1. Approve   — nothing is booked yet; the customer can still cancel.
 *  2. Ready to Ship — the order is placed on Shiprocket and a courier is booked.
 * Both open the full order (items, customer, delivery charge) to check first.
 */

export const READY_TO_SHIP_REMINDER =
	"Please approve and keep ready to ship within 24 hours to avoid delays in shipping. " +
	"Freight charges come from the delivery partner and may change by the time the order ships.";

type Step = "approve" | "ship" | "rebook";

const isCancelled = (status?: string) => /cancel/i.test(status || "");
const activeItems = (order: any): any[] => (order.items || []).filter((it: any) => !isCancelled(it.status));
const hasShipment = (order: any) => !!order.shiprocketShipmentId && String(order.shiprocketShipmentId) !== "0";

/** What the merchant has to do next for this order, if anything. */
export function nextStep(order: any): Step | null {
	if (isCancelled(order.status) || activeItems(order).length === 0) return null;
	const confirmed = /^confirmed$/i.test(order.status || "");
	if (confirmed && !order.merchantApprovedAt) return "approve";
	if (confirmed && !hasShipment(order)) return "ship";
	// On Shiprocket but no courier took it yet: booking again retries the courier.
	if (hasShipment(order) && !order.awbCode && /^new$/i.test(order.status || "")) return "rebook";
	return null;
}

const rupees = (n: number) => `₹${Math.round(n * 100) / 100}`;

type LiveQuote = { courierName?: string; shipping?: number; coverage?: number; whatsapp?: number; total?: number; quotedAt?: string };

// Asked fresh from Shiprocket every time the dialog opens.
function LiveDeliveryCharge({ quote, failed }: { quote: LiveQuote | null; failed: boolean }) {
	if (failed) {
		return (
			<Typography sx={{ fontSize: 13, color: "#A23A00", mt: 1 }}>
				Couldn't get the delivery charge from Shiprocket right now. Close and open again to retry.
			</Typography>
		);
	}
	if (!quote) {
		return (
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
				<CircularProgress size={14} />
				<Typography sx={{ fontSize: 13, color: "#555" }}>Getting the delivery charge from Shiprocket…</Typography>
			</Stack>
		);
	}
	const extra = Number(quote.coverage ?? 0) + Number(quote.whatsapp ?? 0);
	return (
		<Box sx={{ mt: 1, p: 1.25, bgcolor: "#FFF6EC", borderRadius: 1, border: "1px solid #F3D3B0" }}>
			<Row label="Delivery charge from Shiprocket (live)" value={rupees(Number(quote.total ?? 0))} bold />
			<Typography sx={{ fontSize: 12, color: "#555" }}>
				{quote.courierName ? `${quote.courierName} · ` : ""}freight {rupees(Number(quote.shipping ?? 0))}
				{extra > 0 ? ` + other charges ${rupees(extra)}` : ""}
				{quote.quotedAt ? ` · as of ${new Date(quote.quotedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}
			</Typography>
			<Typography sx={{ fontSize: 11, color: "#7A5200", mt: 0.5 }}>
				Today's price from Shiprocket. It can change before the courier is booked, and later if the courier re-weighs the parcel.
			</Typography>
		</Box>
	);
}

function OrderSummary({ order, quote, quoteFailed }: { order: any; quote: LiveQuote | null; quoteFailed: boolean }) {
	const items: any[] = order.items || [];
	const itemsTotal = activeItems(order).reduce(
		(sum, it) => sum + Number(it.totalPrice ?? (it.unitPrice ?? 0) * (it.quantity ?? 1)),
		0
	);
	const coupon = Number(order.couponDiscount ?? 0);
	const delivery = Number(order.deliveryCharge ?? 0);
	const address = order.address || {};
	const cod = String(order.paymentMode || "").toUpperCase() === "COD";
	return (
		<Box sx={{ fontSize: 14 }}>
			<Typography sx={{ fontWeight: 700 }}>Order #{String(order.id || "").slice(-8).toUpperCase()}</Typography>
			<Typography sx={{ fontSize: 13, color: "#666", mb: 1.5 }}>
				{order.createdAt ? new Date(order.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
				{order.paymentMode ? ` · ${order.paymentMode}` : ""}
			</Typography>

			<Typography sx={{ fontSize: 13, fontWeight: 700 }}>Ship to</Typography>
			<Typography sx={{ fontSize: 13 }}>
				{address.fullName}
				{address.mobile ? ` · ${address.mobile}` : ""}
			</Typography>
			<Typography sx={{ fontSize: 13, color: "#444", mb: 1.5 }}>
				{[address.addressLine1, address.addressLine2, address.landmark, address.city, address.state, address.pinCode].filter(Boolean).join(", ")}
			</Typography>

			<Typography sx={{ fontSize: 13, fontWeight: 700 }}>Items</Typography>
			{items.map((it) => {
				const cancelled = isCancelled(it.status);
				return (
					<Stack key={it.id} direction="row" justifyContent="space-between" sx={{ fontSize: 13, color: cancelled ? "#999" : "#222" }}>
						<Typography sx={{ fontSize: 13, textDecoration: cancelled ? "line-through" : "none" }}>
							{it.product?.productName ?? "Item"}
							{it.selectedSize ? ` · ${String(it.selectedSize).toUpperCase()}` : ""} × {it.quantity ?? 1}
							{cancelled ? " (cancelled)" : ""}
						</Typography>
						<Typography sx={{ fontSize: 13 }}>{rupees(Number(it.totalPrice ?? 0))}</Typography>
					</Stack>
				);
			})}
			<Divider sx={{ my: 1 }} />
			<Row label="Items to ship" value={rupees(itemsTotal)} bold />
			{coupon > 0 && <Row label="Coupon" value={`−${rupees(coupon)}`} />}
			<Row label="Delivery charge paid by the customer at checkout" value={delivery > 0 ? rupees(delivery) : "Free"} />
			{cod && <Row label="Courier collects from the customer" value={rupees(Math.max(0, itemsTotal - coupon) + delivery)} bold />}
			<LiveDeliveryCharge quote={quote} failed={quoteFailed} />
		</Box>
	);
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
	return (
		<Stack direction="row" justifyContent="space-between">
			<Typography sx={{ fontSize: 13, fontWeight: bold ? 700 : 400 }}>{label}</Typography>
			<Typography sx={{ fontSize: 13, fontWeight: bold ? 700 : 400 }}>{value}</Typography>
		</Stack>
	);
}

const LABELS: Record<Step, { button: string; title: string; confirm: string; note: string }> = {
	approve: {
		button: "Approve",
		title: "Approve this order?",
		confirm: "Approve order",
		note: "Approving doesn't book the courier yet. Pack the items, then tap Ready to Ship.",
	},
	ship: {
		button: "Ready to Ship",
		title: "Ready to ship?",
		confirm: "Book courier",
		note: "The order is placed with the delivery partner and a courier is booked to collect the parcel. Keep it packed and ready.",
	},
	rebook: {
		button: "Book courier again",
		title: "Book a courier again?",
		confirm: "Book courier",
		note: "No courier took this parcel yet. We'll ask the delivery partner again.",
	},
};

export default function OrderShipActions({ order, onDone, size = "small" }: { order: any; onDone: () => void; size?: "small" | "medium" }) {
	const step = nextStep(order);
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [quote, setQuote] = useState<LiveQuote | null>(null);
	const [quoteFailed, setQuoteFailed] = useState(false);

	// A new live price each time the dialog opens.
	useEffect(() => {
		if (!open || !order?.id) return;
		let active = true;
		setQuote(null);
		setQuoteFailed(false);
		OrdersController.getLiveShippingQuote(order.id)
			.then((res: any) => active && setQuote(res?.data?.data ?? null))
			.catch(() => active && setQuoteFailed(true));
		return () => {
			active = false;
		};
	}, [open, order?.id]);

	if (!step) return null;
	const text = LABELS[step];

	const confirm = async () => {
		setBusy(true);
		try {
			const res: any =
				step === "approve"
					? await OrdersController.approveSubOrder(order.id)
					: await OrdersController.postSubOrderDetails({ subOrderId: order.id } as any);
			const message = res?.data?.data;
			// The booking can succeed on Shiprocket while no courier takes it yet.
			if (typeof message === "string" && /no courier/i.test(message)) toast.warning(message);
			else toast.success(typeof message === "string" ? message : "Done");
			setOpen(false);
			onDone();
		} catch {
			// The error is already shown by the API layer.
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
			<Button
				size={size}
				variant="contained"
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
				sx={{ bgcolor: "#E09145", color: "#fff", textTransform: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: "#c97a30" } }}
			>
				{text.button}
			</Button>
			<Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
				<DialogTitle sx={{ fontWeight: 700 }}>{text.title}</DialogTitle>
				<DialogContent>
					<OrderSummary order={order} quote={quote} quoteFailed={quoteFailed} />
					<Typography sx={{ fontSize: 12, color: "#555", mt: 2 }}>{text.note}</Typography>
					{step !== "rebook" && (
						<Typography sx={{ fontSize: 12, color: "#A23A00", mt: 1, fontWeight: 600 }}>{READY_TO_SHIP_REMINDER}</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: "none", color: "#111" }}>
						Not now
					</Button>
					<Button
						variant="contained"
						onClick={confirm}
						disabled={busy}
						sx={{ bgcolor: "#111", color: "#fff", textTransform: "none", "&:hover": { bgcolor: "#333" } }}
					>
						{busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : text.confirm}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
